import {
  getStoredFbclid,
  getStoredUtmCampaign,
  getStoredUtmSource,
} from "@/components/feature/utm-source-capture";
import { type PostHog, usePostHog } from "posthog-js/react";
import { useEffect } from "react";

const ONE_MINUTE_MS = 60 * 1000;
const FIVE_MINUTES_MS = 5 * ONE_MINUTE_MS;

const META_SOURCE = "meta";
const isMetaSource = (campaignStored: string) =>
  campaignStored.toLowerCase() === META_SOURCE.toLowerCase();

const getLocalEventData = (eventName: string, personId: string) => {
  try {
    const data = JSON.parse(localStorage.getItem(eventName) || JSON.stringify({}));
    data[personId] = data[personId] || { events: {} };
    data[personId].events[eventName] = data[personId].events[eventName] || {
      wasCapturedAt: false,
    };
    return data;
  } catch (_err) {}

  return { [personId]: { events: {} } };
};

export function useGameEngagementTimer(gameSlug: string): void {
  const posthog = usePostHog();

  useEffect(() => {
    const eventName_1min_engagement = "game_engagement_1_minute";
    const eventName_5min_engagement = "game_engagement_5_minute";

    const timeout1Min = window.setTimeout(() => {
      const oneMinInSeconds = ONE_MINUTE_MS / 1000;

      const eventPayload = {
        play_duration_seconds: oneMinInSeconds,
        game_slug: gameSlug,
      };
      sendOneTimeEvent(eventName_1min_engagement, posthog, eventPayload, isMetaSource);
    }, ONE_MINUTE_MS);

    const timeout5Min = window.setTimeout(() => {
      const fiveMinInSeconds = FIVE_MINUTES_MS / 1000;

      const eventPayload = {
        play_duration_seconds: fiveMinInSeconds,
        game_slug: gameSlug,
      };
      sendOneTimeEvent(eventName_5min_engagement, posthog, eventPayload, isMetaSource);
    }, FIVE_MINUTES_MS);

    return () => {
      window.clearTimeout(timeout1Min);
      window.clearTimeout(timeout5Min);
    };
  }, [gameSlug, posthog]);
}

function sendOneTimeEvent(
  eventName: string,
  posthog: PostHog,
  payload: Record<string, unknown>,
  utmSourceIncluded: (campaignStored: string) => boolean
) {
  const personId = posthog.get_distinct_id();
  const fbclid = getStoredFbclid();
  const utmSource = getStoredUtmSource()?.toLowerCase() || "";

  if (!personId || !utmSourceIncluded?.(utmSource) || !fbclid) {
    return;
  }

  const localEventData = getLocalEventData(eventName, personId);
  const eventData = localEventData[personId].events[eventName];
  // If the event was already captured (after May 18th 2026 11:59PM in UTC timezone), don't capture it again
  const captureAfter = "2026-05-19T00:00:00Z";
  if (
    typeof eventData.wasCapturedAt === "string" &&
    new Date(eventData.wasCapturedAt).getTime() > new Date(captureAfter).getTime()
  ) {
    return;
  }
  const utmCampaign = getStoredUtmCampaign() || "";
  const newEventData = {
    ...eventData,
    ...payload,
    ...(fbclid ? { fbclid } : {}),
    ...(utmSource ? { utm_source: utmSource } : {}),
    ...(utmCampaign ? { utm_campaign: utmCampaign } : {}),
    wasCapturedAt: new Date().toISOString(),
  };
  try {
    posthog.capture(eventName, newEventData);
  } catch (err) {
    console.error("Error sending one time event", err);
  }
  // Only persist the current personId's entry so the stored object can't grow
  // unboundedly across different anonymous IDs on the same browser.
  const updatedEvents = {
    ...localEventData[personId].events,
    [eventName]: { ...newEventData, wasCapturedAt: new Date().toISOString() },
  };
  localStorage.setItem(eventName, JSON.stringify({ [personId]: { events: updatedEvents } }));
}
